import React, { createContext, useContext, useEffect, useState } from 'react';
import { customerAuthService } from '@/services/auth';
import { customerApiClient } from '@/services/api';
import type { Customer } from '@shared/types';

interface CustomerAuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  customer: Customer | null;
  login: () => void;
  logout: () => void;
}

const AuthContext = createContext<CustomerAuthContextType | undefined>(undefined);

export const CustomerAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [customer, setCustomer] = useState<Customer | null>(null);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const auth = await customerAuthService.isAuthenticated();
        setIsAuthenticated(auth);
        if (auth) {
          try {
            const me = await customerApiClient.getMe();
            setCustomer(me);
          } catch {
            console.error('Failed to load customer profile');
          }
        }
      } catch {
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    };
    checkAuth();
  }, []);

  const login = () => {
    setIsAuthenticated(true);
  };

  const logout = () => {
    setIsAuthenticated(false);
    setCustomer(null);
    customerAuthService.logout();
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, isLoading, customer, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useCustomerAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useCustomerAuth must be used within a CustomerAuthProvider');
  }
  return context;
};
